import React, { useEffect, useState, useCallback, useRef } from 'react';
import { RefreshCw, CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { toast } from 'sonner';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { notifyImportComplete } from '../../utils/importToast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { isIOSNative } from '../../utils/nativeBridge';

/**
 * iter959 Phase 3 - BacklogProgressBanner
 *
 * Sits at the top of DashboardLayout on every page and polls
 * GET /api/banking/backlog-jobs/active every 6 seconds.
 *
 * Any job whose phase is in {queued, fetching, counting, charging, processing}
 * is rendered as a slim progress banner. When a job transitions to a terminal
 * phase (complete / charge_failed / error) we:
 *   – show a one-off toast with language dependent on `language_variant`
 *   – POST /banking/backlog-jobs/{id}/ack so the row stops appearing
 *
 * If multiple banks are syncing at once we stack them; each has its own X.
 */

const PHASE_LABELS = {
  queued: 'Connecting to your bank…',
  fetching: 'Getting your latest transactions…',
  counting: 'Checking how far back your transactions go…',
  charging: 'Confirming your history import…',
  processing: 'Categorising your transactions…',
  complete: 'Done',
  charge_failed: 'History import needs attention',
  awaiting_consent: 'Older transactions found - your confirmation is needed',
  balance_gap: 'Balance gap found - transactions parked',
  consent_declined: 'History import skipped',
  error: 'Update could not finish',
};

// Backend progress labels are technical ("Classifying transactions (12 of 43)…");
// show them in the same business language as statement uploads.
const humanizeLabel = (label) => {
  if (!label) return '';
  return String(label)
    .replace(/^Classifying transactions \((\d+) of (\d+)\)…?$/i, 'Categorising transaction $1 of $2')
    .replace(/^Preparing to fetch transactions\.{0,3}$/i, 'Connecting to your bank…')
    .replace(/^Storing and classifying/i, 'Categorising')
    .replace(/^Complete! (\d+) auto-posted, (\d+) need review\.?$/i, 'Done - $1 added to your books, $2 waiting for your review');
};

const isTerminal = (p) => p === 'complete' || p === 'charge_failed' || p === 'post_failed' || p === 'error' || p === 'consent_declined' || p === 'balance_gap';

function LanguageForVariant(variant, phase, extras = {}) {
  // Variant: 'initial' | 'subsequent' | 'retry'
  if (phase === 'complete') {
    // Same shape as the statement-upload completion toast: outcome counts + one-tap jump to Review Needed.
    const r = extras.result || {};
    const hasCounts = r.added != null || r.review_needed != null;
    const nothingNew = hasCounts && !(r.added || 0) && !(r.review_needed || 0);
    if (nothingNew && variant !== 'initial') {
      return { title: `${extras.bank || 'Bank'} is up to date`, body: 'No new transactions since your last update.', toast: 'success', reviewAction: false };
    }
    const body = hasCounts
      ? `${(r.added || 0).toLocaleString()} added to your books, ${(r.review_needed || 0).toLocaleString()} need review.`
      : 'Your latest transactions are in the Bank Transactions Center.';
    const title = variant === 'initial'
      ? `Bank connected${extras.bank ? ` - ${extras.bank}` : ''}`
      : variant === 'retry'
        ? 'History import complete'
        : `${extras.bank || 'Bank'} updated`;
    return { title, body, toast: 'success', reviewAction: (r.review_needed || 0) > 0 };
  }
  if (phase === 'balance_gap') {
    return { title: 'Balance gap detected', body: extras.error || 'New transactions are parked until the gap is covered by a statement.', toast: 'error' };
  }
  if (phase === 'consent_declined') {
    return { title: 'Backlog import skipped', body: 'Current-month transactions were posted. You can import the history later from the bank tile.', toast: 'info' };
  }
  if (phase === 'charge_failed') {
    return {
      title: 'Backlog fee could not be charged',
      body: 'We\'ve calculated your opening balance and pushed current-month transactions. To fetch the backlog, please retry from the bank in Bank Accounts.',
      toast: 'error',
    };
  }
  if (phase === 'post_failed') {
    // iter1207 - fee was charged successfully but posting the txns crashed.
    // Distinct copy so the user knows the CHARGE isn't lost, and they can
    // retry the posting via the bank tile.
    return {
      title: 'Backlog fee charged, but posting failed',
      body: 'Your card was charged, but importing the historical transactions hit an error. Retry from the bank tile in Bank Accounts to finish the import — you will NOT be charged again.',
      toast: 'error',
    };
  }
  return {
    title: 'Something went wrong',
    body: 'We could not finish importing your transactions. Please try again from the bank tile.',
    toast: 'error',
  };
}

// Phases whose copy mentions fees / charges. The iOS App Store build must never
// show these (Apple compliance) - everything else renders normally there.
const FEE_PHASES = new Set(['charging', 'awaiting_consent', 'charge_failed', 'post_failed']);

export default function BacklogProgressBanner() {
  // iOS App Store build: render a fee-free variant (progress only). Previously
  // the whole banner was suppressed, so iOS users had NO indication a bank
  // update was still running and acted on half-synced feeds.
  return <BacklogProgressBannerInner feeFree={isIOSNative()} />;
}

function BacklogProgressBannerInner({ feeFree = false }) {
  const [jobs, setJobs] = useState([]);
  const [drawerJobId, setDrawerJobId] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const seenTerminalIds = useRef(new Set());
  const pollTimerRef = useRef(null);
  const drawerTimerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  // Jul 16 - one banner per state per page. The Bank Transactions Center and
  // My Banks pages have their own dedicated sync indicators (blue fetching
  // banner + ClassificationProgressTracker). Rendering this global banner
  // there too meant up to 3 overlapping banners. Polling continues so the
  // completion toast still fires wherever the user is.
  // Jul 16 - suppressed on dedicated bank pages to avoid banner overlap.
  // June 2026 fact-check (user report): /bank-feeds' "dedicated indicators"
  // only cover statement uploads - Mono/Plaid sync jobs had NO indicator
  // there. Re-enabled on /bank-feeds; only /banks keeps its own tile chips.
  // Sep 2026 · This is THE one progress UI for every Mono flow (initial
  // connect, relink-picker, reauth, webhook sync) - shown on every page,
  // including /banks. The per-account ClassificationProgressTracker card
  // no longer renders for Mono-sourced progress.
  const onDedicatedBankPage = false;

  const poll = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/banking/backlog-jobs/active');
      const list = (data?.jobs || []).filter((j) => !(feeFree && FEE_PHASES.has(j.phase)));

      // For any newly-terminal job, fire the toast + ack it
      for (const job of list) {
        if (isTerminal(job.phase) && !seenTerminalIds.current.has(job.id)) {
          seenTerminalIds.current.add(job.id);
          const msg = LanguageForVariant(job.language_variant, job.phase, {
            bank: job.bank_name, error: job.error, result: job.result,
          });
          if (job.phase === 'complete') {
            const r = job.result || {};
            notifyImportComplete({ title: msg.title, added: r.added || 0, reviewNeeded: r.review_needed || 0, extra: r.added == null ? msg.body : '', navigate });
          } else {
            (msg.toast === 'error' ? toast.error : toast.info)(msg.title, { description: msg.body });
          }
          apiClient.post(`/banking/backlog-jobs/${job.id}/ack`).catch(() => {});
        }
      }
      setJobs(list.filter((j) => !isTerminal(j.phase)));
    } catch (e) {
      // Silent - the banner is a background nicety, no user-facing failure
    }
  }, [feeFree]);

  useEffect(() => {
    poll();
    pollTimerRef.current = setInterval(poll, 6000);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [poll]);

  const dismissJob = async (jobId) => {
    // Cosmetic dismiss - job continues running in the background
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
  };

  // ── "See what's syncing" drawer ────────────────────────────────────────
  const loadDrawer = useCallback(async (jobId) => {
    setDrawerLoading(true);
    try {
      const { data } = await apiClient.get(`/banking/backlog-jobs/${jobId}/recent-transactions`);
      setDrawerData(data);
    } catch (_) {
      setDrawerData({ transactions: [], job: null });
    } finally { setDrawerLoading(false); }
  }, []);

  useEffect(() => {
    if (!drawerJobId) return undefined;
    loadDrawer(drawerJobId);
    drawerTimerRef.current = setInterval(() => loadDrawer(drawerJobId), 6000);
    return () => { if (drawerTimerRef.current) clearInterval(drawerTimerRef.current); };
  }, [drawerJobId, loadDrawer]);

  const openDrawer = (jobId) => { setDrawerJobId(jobId); };
  const closeDrawer = () => { setDrawerJobId(null); setDrawerData(null); };

  const fmtAmount = (v, ccy) => {
    const symbol = ccy === 'NGN' ? '₦' : ccy === 'GBP' ? '£' : ccy === 'USD' ? '$' : '';
    const n = typeof v === 'number' ? v / 100 : parseFloat(v || 0);
    return `${symbol}${Number(n || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  };

  if (jobs.length === 0 || onDedicatedBankPage) return null;

  const onBankFeeds = location.pathname.startsWith('/bank-feeds');

  return (
    <>
    {/* Same visual language as the statement-upload banner (GlobalUploadProgressBadge):
        slim full-width strip, bank name + plain-English status, thin bar, "View progress". */}
    <div className="w-full" data-testid="backlog-progress-banner-root">
      {jobs.map((job) => {
        const awaiting = job.phase === 'awaiting_consent';
        const label = PHASE_LABELS[job.phase] || 'Updating…';
        const liveLabel = humanizeLabel(job.progress?.label);
        const fetched = job.progress?.fetched ?? 0;
        const processed = job.progress?.processed ?? 0;
        const total = job.progress?.total ?? 0;
        const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : null;
        const bank = job.bank_name || 'Bank';
        const tone = awaiting
          ? 'bg-amber-50 border-amber-200 text-amber-900'
          : 'bg-indigo-50 border-indigo-200 text-indigo-900';

        return (
          <div key={job.id} data-testid={`backlog-progress-banner-${job.id}`} className={`w-full border-b ${tone}`}>
            <div className="max-w-7xl mx-auto px-3 py-1 flex items-center gap-2">
              <RefreshCw className={`w-3.5 h-3.5 shrink-0 opacity-70 ${awaiting ? '' : 'animate-spin'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs font-medium truncate max-w-[20ch] sm:max-w-[32ch]" data-testid={`backlog-progress-title-${job.id}`}>
                    {bank}{job.account_last4 ? ` ···${job.account_last4}` : ''}
                  </span>
                  <span className="text-xs" data-testid={`backlog-progress-status-${job.id}`}>
                    {awaiting || !liveLabel ? label : liveLabel}{pct !== null ? ` · ${pct}%` : fetched > 0 ? ` · ${fetched.toLocaleString()} received` : ''}
                  </span>
                </div>
                {awaiting ? (
                  <div className="text-[11px] opacity-90" data-testid={`backlog-consent-copy-${job.id}`}>
                    {job.backlog_count} older transactions found. This month is already in your feed - confirm in the dialog to import the rest.
                  </div>
                ) : (
                  <>
                    <div className="mt-0.5 h-0.5 w-full bg-white/60 rounded overflow-hidden" data-testid={`backlog-progress-bar-${job.id}`}>
                      <div className={`h-full bg-indigo-500 transition-all ${pct === null ? 'w-1/3 animate-pulse' : ''}`} style={pct !== null ? { width: `${pct}%` } : undefined} />
                    </div>
                    <div className="text-[11px] opacity-80 mt-0.5" data-testid={`backlog-progress-hint-${job.id}`}>
                      New items are still arriving - hold off on reviewing until this finishes.
                    </div>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => openDrawer(job.id)}
                className="text-[11px] font-medium px-2 py-1 rounded bg-white/80 hover:bg-white border border-current/20 shrink-0"
                data-testid={`backlog-progress-see-${job.id}`}
              >
                View progress
              </button>
              {!onBankFeeds && (
                <Link to="/bank-feeds" className="text-[11px] underline underline-offset-2 hidden sm:inline shrink-0" data-testid={`backlog-progress-open-${job.id}`}>
                  Open Bank Feed
                </Link>
              )}
              {/* Hide = only hides this banner. A synced account's update cannot be cancelled. */}
              <button type="button" aria-label="Hide this banner" title="Hide (the update keeps running)" onClick={() => dismissJob(job.id)} className="text-[11px] text-current/70 hover:text-current shrink-0 underline underline-offset-2" data-testid={`backlog-progress-dismiss-${job.id}`}>
                Hide
              </button>
            </div>
          </div>
        );
      })}
    </div>

    {/* "View progress" - same layout as the statement-upload progress modal:
        status line, progress bar, counters, then the live list of what's coming in. */}
    <Dialog open={!!drawerJobId} onOpenChange={(v) => !v && closeDrawer()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="backlog-progress-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-indigo-600 animate-spin" />
            {drawerData?.job?.bank_name || 'Bank update'}
          </DialogTitle>
          <DialogDescription>
            {humanizeLabel(drawerData?.job?.progress?.label) || PHASE_LABELS[drawerData?.job?.phase] || 'Updating your transactions…'}
          </DialogDescription>
        </DialogHeader>

        {drawerLoading && !drawerData && (
          <div className="py-8 flex items-center justify-center text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        )}

        {drawerData?.job && (() => {
          const pr = drawerData.job.progress || {};
          const t = pr.total || 0; const d = pr.processed || 0;
          const pct = t > 0 ? Math.min(100, Math.round((d / t) * 100)) : null;
          return (
            <div className="space-y-2" data-testid="backlog-progress-summary">
              <div className="h-1.5 w-full bg-slate-100 rounded overflow-hidden">
                <div className={`h-full bg-indigo-500 transition-all ${pct === null ? 'w-1/3 animate-pulse' : ''}`} style={pct !== null ? { width: `${pct}%` } : undefined} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded border bg-slate-50 p-2"><p className="text-[10px] uppercase tracking-wider text-slate-500">Received</p><p className="text-sm font-semibold">{(pr.fetched ?? t ?? 0).toLocaleString()}</p></div>
                <div className="rounded border bg-slate-50 p-2"><p className="text-[10px] uppercase tracking-wider text-slate-500">Categorised</p><p className="text-sm font-semibold">{d.toLocaleString()}</p></div>
                <div className="rounded border bg-slate-50 p-2"><p className="text-[10px] uppercase tracking-wider text-slate-500">Remaining</p><p className="text-sm font-semibold">{Math.max(0, t - d).toLocaleString()}</p></div>
              </div>
            </div>
          );
        })()}

        <div className="mt-2">
          <div className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-indigo-500" /> What&apos;s coming in
          </div>
          {(drawerData?.transactions || []).length === 0 && !drawerLoading && (
            <div className="text-xs text-slate-500 py-4 text-center border border-dashed rounded">
              Nothing yet - we&apos;re still getting your transactions. This updates automatically.
            </div>
          )}
          <ul className="space-y-1.5" data-testid="backlog-progress-tx-tail">
            {(drawerData?.transactions || []).map((t) => (
              <li key={t.id} className="border rounded p-2 text-xs flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{t.narration || t.description || '(no description)'}</div>
                  <div className="text-[10px] text-slate-500">{t.date}</div>
                </div>
                <div className={`font-mono tabular-nums text-right ${t.type === 'credit' ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {t.type === 'credit' ? '+' : '−'}{fmtAmount(t.amount, drawerData?.job?.currency)}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
          You can close this - the update keeps running and we&apos;ll let you know when it&apos;s done.
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
